import { cn } from '@/lib/cn'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface Item {
  label: ReactNode
  onSelect: () => void
  disabled?: boolean
  destructive?: boolean
}

interface Props {
  trigger: ReactNode
  items: Item[]
  align?: 'start' | 'end'
  className?: string
  disabled?: boolean
}

interface Position {
  top: number
  left: number
}

export function Dropdown({ trigger, items, align = 'end', className, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const hasAutoFocused = useRef(false)

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  const updatePosition = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const menuWidth = menuRef.current?.offsetWidth ?? 180
    setPosition({
      top: rect.bottom + 4,
      left: align === 'end' ? rect.right - menuWidth : rect.left,
    })
  }

  useLayoutEffect(() => {
    if (!open) {
      hasAutoFocused.current = false
      return
    }
    updatePosition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useLayoutEffect(() => {
    if (!open || !position || hasAutoFocused.current) return
    const firstEnabled = items.findIndex((it) => !it.disabled)
    if (firstEnabled >= 0) {
      hasAutoFocused.current = true
      requestAnimationFrame(() => itemRefs.current[firstEnabled]?.focus())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    const onReposition = () => updatePosition()
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const enabledIndices = items
      .map((it, i) => (it.disabled ? -1 : i))
      .filter((i) => i >= 0)
    if (enabledIndices.length === 0) return
    const focusedIndex = itemRefs.current.findIndex((el) => el === document.activeElement)
    const pos = enabledIndices.indexOf(focusedIndex)

    const focusAt = (idx: number | undefined) => {
      if (idx === undefined) return
      itemRefs.current[idx]?.focus()
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      focusAt(enabledIndices[(pos + 1 + enabledIndices.length) % enabledIndices.length])
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      focusAt(enabledIndices[(pos - 1 + enabledIndices.length) % enabledIndices.length])
    } else if (e.key === 'Home') {
      e.preventDefault()
      focusAt(enabledIndices[0])
    } else if (e.key === 'End') {
      e.preventDefault()
      focusAt(enabledIndices[enabledIndices.length - 1])
    }
  }

  return (
    <div className={cn('inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center disabled:cursor-not-allowed"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              onKeyDown={onMenuKeyDown}
              style={{ top: position.top, left: position.left }}
              className="fixed z-50 min-w-[180px] overflow-hidden rounded-xl border border-border bg-background shadow-lg"
            >
              <div className="flex flex-col py-1">
                {items.map((it, i) => (
                  <button
                    key={i}
                    ref={(el) => {
                      itemRefs.current[i] = el
                    }}
                    type="button"
                    role="menuitem"
                    disabled={it.disabled}
                    onClick={() => {
                      if (it.disabled) return
                      close(false)
                      it.onSelect()
                    }}
                    className={cn(
                      'px-3 py-2 text-left text-sm transition-colors disabled:opacity-50',
                      'focus-visible:bg-muted focus-visible:outline-none',
                      it.destructive
                        ? 'text-destructive hover:bg-muted'
                        : 'text-foreground hover:bg-muted',
                    )}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
